<template>
    <div
            v-anime="{ opacity: [0, 1], duration: 600, easing: 'easeInOutSine' }"
            class="sb_lessons_countdown"
    >
        <div class="sb_lesson_info">
            <div class="sb_spacer" />
            <div
                    v-anime="{ ...animations.lessonImage, delay: 1000 }"
                    ref="lessonImage"
                    class="sb_lesson_image"
            >
                <div class="sb_lesson_image_backplane" />
                <figure
                        :style="{ background: `url(${currentLesson.image})` }"
                        class="sb_lesson_image_figure"
                />
            </div>
            <div class="sb_lesson_info_content">
                <div
                        v-anime="{
                            opacity: [0, 1],
                            duration: 1000,
                            delay: 1600,
                            easing: 'easeOutCubic'
                        }"
                        class="sb_lesson_text"
                >
                    {{
                        lesson.itemType === 'buiten'
                            ? 'Live buitenles'
                            : lesson.itemType === 'virtual'
                            ? 'Virtuele groepsles'
                            : 'Live groepsles'
                    }}
                    <icon
                        :id="'icon_' + lesson.itemType"
                        :class="'sb_groepslessen_' + lesson.itemType"
                    />
                </div>
                <div
                        v-anime="{ ...animations.lessonName, delay: 1900 }"
                        ref="lessonName"
                        class="sb_lesson_name"
                >
                    {{ currentLesson.meta.naam }}
                </div>
                <div class="sb_lesson_characteristics">
                    <div
                            v-anime="
                                animations.characteristicContainer(index, 2200, 200)
                            "
                            v-for="(property, index) in currentLesson.kenmerken"
                            ref="characteristicContainer"
                            :key="property.attribuut"
                            class="sb_lesson_characteristic"
                    >
                        <div class="sb_lesson_characteristic_icon">
                            <icon :id="'icon_' + property.attribuut" />
                        </div>
                        <div class="sb_lesson_characteristic_value-box">
                            <div
                                    v-anime="
                                        animations.characteristicIndicator(
                                            property.waarde,
                                            index,
                                            2500,
                                            200
                                        )
                                    "
                                    ref="characteristicIndicator"
                                    class="sb_lesson_characteristic_value-box_indicator"
                            />
                            <div
                                    class="sb_lesson_characteristic_value-box_text"
                            >
                                {{
                                    dutchCharacteristicNameMapping[
                                        property.attribuut
                                    ]
                                }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="sb_structure_progress_container">
              <div
                      :style="{ opacity: lessons.length > 1 ? 1 : 0 }"
                      class="sb_structure_progress"
              >
                  <progress-bar
                          v-for="i in lessons.length"
                          ref="progressBars"
                          :key="'progress-bar' + i"
                          :max-width="100"
                          @done="advanceToNextSlide(i)"
                  />
                  <div
                      :style="{ opacity: lessons.length > 1 ? 1 : 0 }"
                      class="sb_sets_progress"
                  >
                    {{ currentSet }}/{{ maxSet }}
                  </div>
              </div>
            </div>
        </div>
        <div
                v-anime="{
                    translateX: ['100%', '0'],
                    duration: 1000,
                    easing: 'easeOutCubic'
                }"
                class="sb_lesson_info_right"
        >
            <div class="sb_lesson_countdown_state">
                <div class="sb_lesson_countdown_state_time-remaining_icon">
                    <icon id="icon_tijd" />
                </div>
                <div
                        :class="{ 's_lesson-started': lessonStarted, 's_is-upcoming': !currentLesson.isStarting }"
                        class="sb_lesson_countdown_state_time-remaining"
                >
                    {{ formattedTimeRemaining }}
                </div>
                <div
                        ref="lessonPeriod"
                        class="sb_lesson_countdown_state_timespan"
                >
                    {{ currentLesson.startDate | time }} -
                    {{ currentLesson.endDate | time }}
                </div>
                <div
                        ref="lessonLocation"
                        class="sb_lesson_countdown_state_location"
                >
                    {{ currentLesson.zaal.naam }}
                </div>
            </div>
            <div class="sb_lesson_occupation">
                <div class="sb_lesson_occupation_icon">
                    <icon id="icon_groepslessen" />
                </div>
                <div class="sb_lesson_occupation_remaining">
                  <template v-if="occupancyFormat === 'relative'">
                    <span class="sb_lesson_occupation_remaining_number">
                      {{ currentLesson.percentage }}%
                    </span>
                  </template>
                  <template v-else>
                    <span class="sb_lesson_occupation_remaining_number">
                        {{
                            Number(currentLesson.plekken) -
                                Number(currentLesson.remaining)
                        }}
                    </span>
                    &nbsp;/{{ currentLesson.plekken }}
                  </template>
                </div>
                <div class="sb_lesson_occupation_remaining_text">
                    plaatsen
                    <br />
                    bezet
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import format from 'date-fns/format';
import { addMinutes, formatRelative } from 'date-fns';
import { nl } from 'date-fns/locale'

import ProgressBar from '@/components/ProgressBar';

const OccupancyFormats = ['relative', 'absolute']

export default {
    components: {
        ProgressBar
    },
    filters: {
        time: date => {
            return (
                date.getHours() + ':' + String(date.getMinutes()).padEnd(2, '0')
            );
        }
    },
    props: {
        lessons: {
            type: Array,
            required: true,
            validator: value => {
                // At least one element is required
                return value.length;
            }
        },
        lessonInterval: {
            type: Number,
            required: true
        },
        currentTime: {
            type: Date,
            required: true
        },
        currentSet: {
          type: Number,
          required: true
        },
        maxSet: {
          type: Number,
          required: true
        },
        occupancyFormat: {
          type: String,
          required: true,
          validator(value) {
            return OccupancyFormats.includes(value);
          }
        }
    },
    data() {
        return {
            timeRemaining: new Date(this.lessons[0].datum - this.currentTime),
            dutchCharacteristicNameMapping: {
                intensity: 'intensiteit',
                strength: 'kracht',
                endurance: 'uithoudingsvermogen',
                mobility: 'mobiliteit',
                flexibility: 'flexibiliteit'
            },
            lessonIndex: 0,
            currentLesson: null,
            animations: {
                lessonName: {
                    opacity: [0, 1],
                    duration: 1500,
                    easing: 'easeOutCubic'
                },
                lessonImage: {
                    opacity: [0, 1],
                    scale: [1.1, 1],
                    duration: 1400,
                    easing: 'easeInOutSine'
                },
                characteristicContainer: (
                    index,
                    baseDelay = 0,
                    delayIncrement = 200
                ) => ({
                    opacity: [0, 1],
                    translateY: { value: [25, 0], duration: 600 },
                    duration: 1000,
                    delay: baseDelay + index * delayIncrement,
                    easing: 'easeOutCubic'
                }),
                characteristicIndicator: (
                    value,
                    index,
                    baseDelay = 0,
                    delayIncrement = 200
                ) => ({
                    width: `${value}%`,
                    duration: 1000,
                    delay: baseDelay + index * delayIncrement,
                    easing: 'easeOutCubic'
                }),
                lessonPeriod: {
                    opacity: [0, 1],
                    duration: 600,
                    easing: 'easeOutCubic'
                },
                lessonLocation: {
                    opacity: [0, 1],
                    duration: 600,
                    easing: 'easeOutCubic'
                }
            }
        };
    },
    computed: {
        lessonStarted() {
            return this.timeRemaining <= 0;
        },
        formattedTimeRemaining() {
            if (this.currentLesson.isStarting) {
                if (this.timeRemaining <= 0) {
                  return 'begint nu!';
                }
                return format(this.timeRemaining, 'mm:ss');
            }
            const [relativeDay, at] = formatRelative(this.currentLesson.startDate, this.currentTime, {
                weekStartsOn: 1,
                locale: nl
            }).split(' ');
            return `${relativeDay} ${at}`;
        },
        lesson() {
            return this.lessons[this.lessonIndex];
        }
    },
    watch: {
        lesson: {
            immediate: true,
            handler(newLesson, oldLesson) {
                if (oldLesson == null) {
                    this.currentLesson = newLesson;
                    this.$anime({
                        targets: this.currentLesson,
                        remaining: [
                            this.currentLesson.plekken,
                            this.currentLesson.plekken -
                                this.currentLesson.bezet
                        ],
                        percentage: [0, this.currentLesson.percentage],
                        round: true,
                        duration: 1200,
                        easing: 'easeOutQuad',
                        delay: 500
                    });
                    return;
                }

                if(oldLesson.id !== newLesson.id){
                    this.animate(newLesson, oldLesson);
                }
            }
        }
    },
    mounted() {
        this.$refs.progressBars[this.lessonIndex].start(this.lessonInterval);
        const countdownInterval = setInterval(() => {
            const timeRemaining = new Date(
                (this.timeRemaining.getMinutes() * 60 +
                    this.timeRemaining.getSeconds() -
                    1) *
                    1000
            );
            if (timeRemaining <= 0) {
                this.timeRemaining = new Date(0);
                return;
            }
            this.timeRemaining = timeRemaining;
        }, 1000);
        this.$once('hooks:beforeDestroy', () => {
            clearInterval(countdownInterval);
        });
    },
    methods: {
        advanceToNextSlide(nextIndex) {
            if (nextIndex === this.lessons.length) {
                this.$root.$emit('cycle-finished', 'groepslessen');
                this.$refs.progressBars.forEach(progressBar =>
                    progressBar.reset()
                );
                this.lessonIndex = 0;
                this.$refs.progressBars[this.lessonIndex].start(
                    this.lessonInterval
                );
                return;
            }
            this.lessonIndex = nextIndex;
            this.$refs.progressBars[nextIndex].start(this.lessonInterval);
        },
        animate(newLesson, oldLesson) {
            const reverseCopyOfArray = array => [...array].reverse();

            this.$anime
                .timeline({
                    complete: () => {
                        this.currentLesson = {
                            ...newLesson,
                            remaining: newLesson.plekken - newLesson.bezet
                        };
                    }
                })
                /**
                 * Lesson name
                 */
                .add({
                    ...this.animations.lessonName,
                    targets: this.$refs.lessonName,
                    opacity: reverseCopyOfArray(
                        this.animations.lessonName.opacity
                    ),
                    duration: 300,
                    complete: () => {
                        this.currentLesson.meta = {
                            ...this.currentLesson.meta,
                            naam: newLesson.meta.naam
                        };
                    }
                })
                .add({
                    ...this.animations.lessonName,
                    targets: this.$refs.lessonName,
                    offset: '+=300'
                })
                /**
                 * Lesson image
                 */
                .add({
                    ...this.animations.lessonImage,
                    targets: this.$refs.lessonImage,
                    duration: 300,
                    opacity: reverseCopyOfArray(
                        this.animations.lessonImage.opacity
                    ),
                    scale: reverseCopyOfArray(
                        this.animations.lessonImage.scale
                    ),
                    offset: 0,
                    complete: () => {
                        this.currentLesson.image = newLesson.image;
                    }
                })
                .add({
                    ...this.animations.lessonImage,
                    targets: this.$refs.lessonImage,
                    offset: 300
                })
                /**
                 * Availability
                 */
                .add({
                    targets: this.currentLesson,
                    remaining: [
                        oldLesson.remaining,
                        newLesson.plekken - newLesson.bezet
                    ],
                    plekken: [oldLesson.plekken, newLesson.plekken],
                    percentage: [oldLesson.percentage, newLesson.percentage],
                    round: true,
                    duration: 1400,
                    easing: 'easeOutQuad',
                    offset: 0
                })
                /**
                 * Period
                 */
                .add({
                    ...this.animations.lessonPeriod,
                    targets: this.$refs.lessonPeriod,
                    opacity: reverseCopyOfArray(
                        this.animations.lessonPeriod.opacity
                    ),
                    offset: 0,
                    complete: () => {
                        this.currentLesson.startDate = newLesson.startDate;
                        this.currentLesson.endDate = newLesson.endDate;
                    }
                })
                .add({
                    ...this.animations.lessonPeriod,
                    targets: this.$refs.lessonPeriod,
                    opacity: this.animations.lessonPeriod.opacity,
                    offset: 900
                })
                /**
                 * location
                 */
                .add({
                    ...this.animations.lessonLocation,
                    targets: this.$refs.lessonLocation,
                    opacity: reverseCopyOfArray(
                        this.animations.lessonLocation.opacity
                    ),
                    offset: 0,
                    complete: () => {
                        this.currentLesson.zaal = {
                            ...newLesson.zaal,
                            naam: newLesson.zaal.naam
                        };
                    }
                })
                .add({
                    ...this.animations.lessonLocation,
                    targets: this.$refs.lessonLocation,
                    opacity: this.animations.lessonLocation.opacity,
                    offset: 900
            });

            newLesson.kenmerken.forEach((characteristic, index) => {
                this.$anime({
                    ...this.animations.characteristicIndicator(
                        characteristic.waarde,
                        index,
                        600,
                        100
                    ),
                    width: [
                        `${oldLesson.kenmerken[index].waarde}%`,
                        `${characteristic.waarde}%`
                    ],
                    targets: this.$refs.characteristicIndicator[index]
                });
            });
        }
    }
};
</script>
<style lang="scss">
.sb_lessons_countdown {
    width: 100%;
    height: 100%;
    display: flex;

    background-color: rgba($brand-primary-darker, 1);
}

.sb_lesson_info {
    position: relative;
    display: flex;
    flex: 1 auto;

    background-color: rgba($brand-primary-darker, 1);
}

.sb_lesson_info_content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 4;

    padding: 10rem;

    display: flex;
    flex-direction: column;
    justify-content: center;

    transform: skew(-2deg) rotate(-2deg);
}

.sb_lesson_text {
    @include font-headings-bold;
    text-transform: uppercase;
    font-size: 3rem;
    color: rgba($brand-white, 0.4);

    padding: 0 0 2rem 0;

    display: flex;
    align-items: center;

    .sb_groepslessen_live {
        transform: scale(1.2);
    }

    .sb_groepslessen_virtual {
        svg {
            stroke-width: 0.4;
            stroke: #fff;
        }
    }

    svg {
        fill: $brand-white;
        opacity: 0.4;
        width: 1.7em;
        margin-left: 1.5rem;
        height: 1.7em;
    }
}

.sb_lesson_name {
    @include font-headings-bold;
    text-transform: uppercase;
    font-size: 10rem;
}

.sb_lesson_characteristics {
    padding-top: 10rem;

    display: grid;
    grid-template-rows: repeat(5, 1fr);
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: column;
}

.sb_lesson_characteristic {
    display: flex;
    align-items: center;

    padding: 0 5rem 1.6rem 0;
}

.sb_lesson_characteristic_icon {
    flex: 0 auto;
    padding-right: 2rem;
    svg {
        fill: $brand-primary-lightest;
        width: 4.6rem;
        height: 4.6rem;
    }
}

.sb_lesson_characteristic_value-box {
    position: relative;
    flex: 1 auto;

    padding: 1rem 3rem;
    border: 2px solid $brand-white;

    &_indicator {
        position: absolute;
        height: 100%;
        top: 0;
        left: 0;
        background-color: rgba($brand-white, 0.35);
    }

    &_text {
        text-transform: uppercase;
        font-size: 1.6rem;
        line-height: 1.6rem;
        letter-spacing: 0.2rem;
        transform: translateY(2px);
    }
}

.sb_spacer {
    flex: 1 0 25%;
}

.sb_lesson_image {
    height: 100%;
    width: 100%;
    position: relative;
    flex: 0 1 auto;

    &::before {
        content: '';
        position: absolute;
        z-index: 3;
        top: 0;
        // The gradient somehow is not perfectly aligned to the left of the image when it is animating in.
        // I suspect this has something to do with the scale animation.
        left: -1%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
            270deg,
            rgba($brand-primary, 0),
            rgba($brand-primary-darker, 1)
        );
    }

    &_figure {
        width: 100%;
        height: 100%;
        mix-blend-mode: screen;
        filter: grayscale(100%);

        background-repeat: no-repeat !important;
        background-size: cover !important;
        background-position: 50% 50% !important;
    }
}

.sb_structure_progress_container {
  width: 100%;
  position: absolute;
  bottom: 0;
  display: flex;
  justify-content: center;

  .sb_structure_progress {
    position: relative;
    width: auto;
  }
}

// Position element relative to centered element: https://stackoverflow.com/questions/34233556/how-do-you-position-an-element-relative-to-centered-sibling
.sb_sets_progress {
  position: absolute;
  bottom: 0;
  left: 100%;
  padding: 0 0 1.7vh 1.7rem;

  color: rgba($brand-white, 0.35);
  font-size: 3rem;
}

.sb_lesson_info_right {
    flex: 0 1 18.75%;
    display: flex;
    flex-direction: column;
    z-index: 3;
}

.sb_lesson_countdown_state {
    @include font-body-thin;

    flex: 0 0 50%;
    background-color: $brand-secondary;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    &_time-remaining {
        @include font-headings-bold;

        padding: 2.6rem 1.6rem 1.6rem 1.6rem;

        font-size: 8rem;
        line-height: 8rem;
        font-variant-numeric: tabular-nums lining-nums;
        text-align: center;

        &.s_lesson-started {
            font-size: 5.3rem;
            text-transform: uppercase;
        }
        &.s_is-upcoming {
            font-size: 4.3rem;
            line-height: 1;
            text-transform: uppercase;
        }

        &_icon {
            svg {
                fill: rgba($brand-white, 0.6);
                width: 5.6rem;
                height: 5.6rem;
            }
        }
    }

    &_timespan {
        color: rgba($brand-white, 0.6);
        font-size: 3rem;
        padding-bottom: 1.6rem;
        text-align: center;
        font-variant-numeric: tabular-nums lining-nums;
    }

    &_location {
        color: rgba($brand-white, 0.6);
        font-size: 4rem;
        text-transform: lowercase;
    }
}

.sb_lesson_occupation {
    flex: 0 0 50%;
    background-color: $brand-secondary-lightest;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    color: rgba($brand-primary, 0.4);

    &_icon {
        svg {
            fill: rgba($brand-primary, 0.4);
            width: 5.6rem;
            height: 5.6rem;
        }
    }

    &_remaining {
        @include font-body-thin;

        display: flex;
        align-items: center;
        padding: 2.6rem;

        font-size: 6rem;

        &_number {
            @include font-headings-bold;
            font-size: 8rem;
            color: $brand-secondary;
            transform: translateY(5px);
        }

        &_text {
            @include font-body-thin;
            font-size: 4rem;
            text-align: center;
        }
    }
}
</style>